import { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { M } from '@/utils/Motion';
import HomeButton from '@/components/HomeButton';
import BaseButton from '@/components/BaseButton/BaseButton';
import { getCampaignTranslations } from '@/redux/campaign/selectors';
import { useNavigateQuery } from '@/hooks';
import Survey from '@/components/Survey';
import Instruction from '@/components/Instruction';
import { pushEvent } from '@CS/hooks';
import { addPlexureTags } from '@/services/app/mcd';
import API from '@/services/api';
import { getUserEntityData } from '@/redux/user/selectors';
import CSS from './Onboarding.module.css';
import type { OnboardingProps } from './types.d';
import {
  STEPS,
  PLEXURE_TAG_BY_OPTION_KEY,
  EVENT_LABEL_SCREEN_2,
  EVENT_LABEL_SCREEN_3,
  ACTION_BY_KEY_SCREEN_1,
  ACTION_BY_KEY_SCREEN_2,
} from './config';

type OnboardingComponentProps = OnboardingProps & {
  userData?: object;
};

const Onboarding = ({ translations, userData }: OnboardingComponentProps) => {
  const surveyComplete = (userData as { surveyComplete?: boolean } | undefined)?.surveyComplete;
  const [stepIndex, setStepIndex] = useState(0);
  const [answersByQuestion, setAnswersByQuestion] = useState<Record<string, string[]>>({});
  const [selectedPlexureTags, setSelectedPlexureTags] = useState<string[]>([]);
  const navigate = useNavigateQuery();
  const prevStepIndexRef = useRef(0);
  const tagsSentRef = useRef(false);

  const step = STEPS[stepIndex];
  const totalSteps = STEPS.length;

  const isScreen1 = step.type === 'survey' && step.questionKey === 'surveystep1Question';
  const isScreen2 = step.type === 'survey' && step.questionKey === 'surveystep2Question';

  useEffect(() => {
    if (surveyComplete) {
      navigate('/intro-ready');
    }
  }, []);

  const selected = useMemo(() => {
    if (step.type !== 'survey') return [];
    return answersByQuestion[step.questionKey] ?? [];
  }, [answersByQuestion, step]);

  const goNext = () => {
    if (isScreen1) {
      pushEvent({
        event: 'button_click',
        eventAction: '/fcg26/volgende',
        eventLabel: '/fcg26/2screengame'
      });
    }
    if (isScreen2) {
      pushEvent({
        event: 'button_click',
        eventAction: '/fcg26/volgende',
        eventLabel: '/fcg26/3screengame'
      });

      if (!tagsSentRef.current && selectedPlexureTags.length > 0) {
        tagsSentRef.current = true;
        console.info('[Plexure tags] sending batch', selectedPlexureTags);
        void addPlexureTags(selectedPlexureTags);
      }
      void API.updateUser({ data: { surveyComplete: true } })
        .catch(error => console.error('[updateUser] failed', error));
    }

    if (stepIndex < totalSteps - 1) {
      setStepIndex(s => {
        prevStepIndexRef.current = s;
        return s + 1;
      });
    } else {
      navigate('/intro-ready');
    }
  };

  const toggleOption = (key: string) => {
    if (step.type !== 'survey') return;
    const questionKey = step.questionKey;
    const wasSelected = (answersByQuestion[questionKey] ?? []).includes(key);

    if (isScreen1) {
      const eventAction = ACTION_BY_KEY_SCREEN_1[key] ?? '/fcg26/unknown';
      pushEvent({
        event: 'button_click',
        eventAction,
        eventLabel: EVENT_LABEL_SCREEN_2
      });
    }
    if (isScreen2) {
      const eventAction = ACTION_BY_KEY_SCREEN_2[key] ?? '/fcg26/unknown';
      pushEvent({
        event: 'button_click',
        eventAction,
        eventLabel: EVENT_LABEL_SCREEN_3
      });
    }

    setAnswersByQuestion(prev => {
      const current = prev[questionKey] ?? [];
      const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      return { ...prev, [questionKey]: next };
    });

    const plexureTag = PLEXURE_TAG_BY_OPTION_KEY[key];
    if (plexureTag) {
      setSelectedPlexureTags(prev => {
        if (wasSelected) {
          const next = prev.filter(t => t !== plexureTag);
          console.info('[Plexure tags] selection changed', next);
          return next;
        }
        if (prev.includes(plexureTag)) return prev;
        const next = [...prev, plexureTag];
        console.info('[Plexure tags] selection changed', next);
        return next;
      });
    }
  };

  return (
    <M.main
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
      <section className={`${CSS.screen} red-screen-container`}>
        <div className={CSS.header}>
          <span className={CSS.counter}>{stepIndex + 1}/{totalSteps}</span>
          <HomeButton style={{ position: 'absolute', right: 0, top: '-12px' }} />
        </div>

        <M.div
          key={stepIndex}
          initial={{
            opacity: 0,
            x: stepIndex >= prevStepIndexRef.current ? 18 : -18,
          }}
          animate={{ opacity: 1, x: 0 }}
          exit={{
            opacity: 0,
            x: stepIndex >= prevStepIndexRef.current ? -18 : 18,
          }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          style={{ width: '100%' }}
        >
          {step.type === 'survey' && (
            <Survey
              step={step}
              selected={selected}
              translations={translations}
              onToggle={toggleOption}
            />
          )}

          {step.type === 'instruction' && (
            <Instruction translations={translations} />
          )}
        </M.div>

        <div className={CSS.actions}>
          <BaseButton
            text={step.type === 'instruction' ? translations.startletsGo : translations.surveynextBtn}
            onClick={goNext}
            className={CSS.button}
          />
        </div>
      </section>
    </M.main>
  );
};

const mapStateToProps = (state: never) => ({
  translations: getCampaignTranslations(state),
  userData: getUserEntityData(state),
});

export default connect(mapStateToProps)(Onboarding);
